/**
 * UserGroupsAdmin.tsx — Admin > User Groups.
 *
 * Laid out like every Admin list (Lead Sources is the reference — see
 * components/admin/AdminList.tsx), with the corp User Groups toolbar and
 * columns less Account / Accounts:
 *   - "Add Group" top right in the header;
 *   - search by name, with Show deleted at the far right (corp's only other
 *     filter is Account, so there is no filter row);
 *   - one column, User Group Name (sorts from its title, A–Z to start), then
 *     Edit / Delete icons (Restore on a deleted group); a click on a live row
 *     opens its edit form;
 *   - 25 rows a page; while every group fits, the footer shows only the count.
 *
 * On a Locked page (useAdminPageLevel) there is no Add, no row icon and no
 * Actions column; a live group still opens, as a viewer (its name and members,
 * which can be looked through but not changed) with a single Close.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import type { SearchableMultiSelectOption } from '@/components/ui/SearchableMultiSelect';
import { Spinner } from '@/components/ui/Spinner';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { useListPagination } from '@/components/admin/useListPagination';
import { useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { toast } from '@/store/toastStore';
import * as adminSvc from '@/services/admin.service';
import type { UserGroup, AdminUser } from '@/services/admin.service';

// The corp User Groups columns, less Accounts.
const COLUMNS: AdminListColumn[] = [
  { key: 'name', header: 'User Group Name', sortable: true },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

/** The list's own order: A–Z by name. */
const SORT_COMPARE: ListSortCompare<UserGroup> = {
  name: (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
};

// ─── Modal ─────────────────────────────────────────────────────────────────────

interface UserGroupModalProps {
  userGroup?: UserGroup | null;
  allUsers: AdminUser[];
  onClose: () => void;
  onSaved: (saved: UserGroup) => void;
}

/**
 * Exported for `UserGroupsAdmin.groupPermissions.test.tsx`, which mounts it
 * directly: the destructive-sync guard below is only observable from inside
 * this component, and driving it through the page would collide with the
 * exhaustive service mock in `adminTables.mobile.test.tsx`.
 */
export function UserGroupModal({ userGroup, allUsers, onClose, onSaved }: UserGroupModalProps) {
  const isEdit = userGroup != null;
  // Locked: a viewer — fields read-only, Close only, and nothing ever saves.
  const locked = useAdminPageLevel() === 'locked';

  const [name, setName] = useState(() => (isEdit ? userGroup.name : ''));
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  // Kept although the picker is gone: these round-trip the server's current
  // grants straight back on save. See the guard in handleSubmit.
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([]);
  const [loadingRelations, setLoadingRelations] = useState(isEdit);
  /**
   * Whether the arrays above actually came back from the server. A new group
   * has nothing to load, so it starts true; an edit only flips it on success.
   */
  const [relationsLoaded, setRelationsLoaded] = useState(!isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userOptions: SearchableMultiSelectOption[] = useMemo(
    () =>
      allUsers
        .filter((u) => u.active)
        .sort((a, b) => {
          const cmp = a.lastName.localeCompare(b.lastName);
          return cmp !== 0 ? cmp : a.firstName.localeCompare(b.firstName);
        })
        .map((u) => ({
          value: String(u.userId),
          label: `${u.lastName}, ${u.firstName}`,
        })),
    [allUsers],
  );

  useEffect(() => {
    // Guard on userGroup, not only isEdit: the two move together at runtime but
    // nothing tells the compiler that, so both reads of userGroup.roleId below
    // were 'possibly null or undefined'.
    if (!isEdit || !userGroup) return;
    // Captured before the closure: the guard above narrows `userGroup` here,
    // but not inside fetchRelations — a closure can run after the variable has
    // changed, so TS will not carry the narrowing across it.
    const group = userGroup;
    let cancelled = false;

    async function fetchRelations() {
      try {
        const [members, perms] = await Promise.all([
          adminSvc.listGroupMembers(group.roleId),
          adminSvc.listGroupPermissions(group.roleId),
        ]);
        if (cancelled) return;
        setSelectedMemberIds(members.map(String));
        setSelectedPermissionIds(perms.map(String));
        setRelationsLoaded(true);
      } catch {
        if (!cancelled) setError('Failed to load group members/permissions.');
      } finally {
        if (!cancelled) setLoadingRelations(false);
      }
    }

    void fetchRelations();
    return () => { cancelled = true; };
  }, [isEdit, userGroup]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || locked) return;

    setSaving(true);
    setError(null);
    try {
      let roleId: number;

      if (isEdit) {
        roleId = userGroup.roleId;
        await adminSvc.updateUserGroup(roleId, { name: trimmed });
      } else {
        const res = await adminSvc.createUserGroup(trimmed);
        roleId = res.id;
      }

      // Both syncs are diff-based and delete every id we leave out — a real
      // DELETE, not the endDate soft-end used elsewhere. If the load never
      // came back these arrays are still empty, and saving the title would
      // take the group's members and grants with it. Skip them instead.
      if (relationsLoaded) {
        await Promise.all([
          adminSvc.syncGroupMembers(roleId, selectedMemberIds.map(Number)),
          adminSvc.syncGroupPermissions(roleId, selectedPermissionIds.map(Number)),
        ]);
      }

      if (!relationsLoaded) {
        // The title saved, but we deliberately skipped the member sync. Say so:
        // a bare "Group updated" would read as though the members saved too.
        toast.success('Group name saved. Members were left unchanged — reopen the group to edit them.');
      } else {
        toast.success(isEdit ? 'Group updated' : 'Group created');
      }
      onSaved({
        roleId,
        accountId: isEdit ? userGroup.accountId : 0,
        name: trimmed,
        active: isEdit ? userGroup.active : true,
        rOrder: isEdit ? userGroup.rOrder : 0,
      });
      onClose();
    } catch {
      setError('Failed to save user group. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={locked ? 'User Group' : isEdit ? 'Edit User Group' : 'Add User Group'}
      size="lg"
      footer={
        locked ? (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={saving || loadingRelations || !name.trim()}
            loading={saving}
          >
            {isEdit ? 'Save Changes' : 'Add Group'}
          </Button>
        )
      }
    >
      <AdminLockedFieldset>
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}

          <FormField label="Title" required={!locked}>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Surgeons"
              required
              autoFocus
            />
          </FormField>

          {loadingRelations ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="md" label="Loading members & permissions…" />
            </div>
          ) : (
            <FormField label="Users">
              <SearchableMultiSelect
                options={userOptions}
                selectedValues={selectedMemberIds}
                onSelectionChange={setSelectedMemberIds}
                placeholder="No users"
                searchPlaceholder="Search users…"
                emptyMessage="No active users found."
              />
            </FormField>
          )}
        </form>
      </AdminLockedFieldset>
    </Modal>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export function UserGroupsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [allItems, setAllItems] = useState<UserGroup[]>([]);
  const [allUsers, setAllUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState<UserGroup | null>(null);
  const [deleteItem, setDeleteItem] = useState<UserGroup | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [groups, users] = await Promise.all([
        adminSvc.listUserGroups(),
        adminSvc.listUsers(),
      ]);
      setAllItems(groups);
      setAllUsers(users);
    } catch {
      toast.error('Failed to load user groups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const query = search.trim().toLowerCase();
  const visibleItems = useMemo(
    () =>
      (showDeleted ? allItems : allItems.filter((g) => g.active))
        .filter((g) => query === '' || g.name.toLowerCase().includes(query))
        .slice()
        .sort(SORT_COMPARE.name),
    [allItems, showDeleted, query],
  );

  const sorting = useListSort(visibleItems, { initialKey: 'name', compare: SORT_COMPARE });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted] });

  function handleCreated(saved: UserGroup) {
    setAllItems((prev) => [...prev, saved]);
  }

  function handleUpdated(saved: UserGroup) {
    setAllItems((prev) =>
      prev.map((g) => (g.roleId === saved.roleId ? { ...g, ...saved } : g)),
    );
    setEditItem(null);
  }

  async function handleDelete() {
    if (!deleteItem) return;
    try {
      await adminSvc.softDeleteUserGroup(deleteItem.roleId);
      setAllItems((prev) =>
        prev.map((g) =>
          g.roleId === deleteItem.roleId ? { ...g, active: false } : g,
        ),
      );
      toast.success('Group deleted');
    } catch {
      toast.error('Failed to delete group');
    }
    setDeleteItem(null);
  }

  async function handleRestore(item: UserGroup) {
    try {
      await adminSvc.restoreUserGroup(item.roleId);
      setAllItems((prev) =>
        prev.map((g) =>
          g.roleId === item.roleId ? { ...g, active: true } : g,
        ),
      );
      toast.success('Group restored');
    } catch {
      toast.error('Failed to restore group');
    }
  }

  return (
    <AdminListPage
      title="User Groups"
      subtitle="Manage user groups for access control and task assignment."
      actions={
        <Button onClick={() => setShowCreate(true)} leftIcon={<Plus className="h-4 w-4" />}>
          Add Group
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search user groups…"
          searchLabel="Search user groups"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="user-groups-show-deleted"
        />
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading user groups…" />
      ) : visibleItems.length === 0 ? (
        <AdminListEmpty
          title="No user groups found"
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add your first user group to organize staff members.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="User groups" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((row) => {
              const deleted = !row.active;
              return (
                <tr
                  key={row.roleId}
                  data-user-group-id={row.roleId}
                  className={adminRowClass({ deleted, clickable: !deleted })}
                  // A live row opens its form; a deleted one is restored, not edited.
                  onClick={deleted ? undefined : () => setEditItem(row)}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName deleted={deleted} nameTestId="user-group-name">
                      {row.name}
                    </AdminRowName>
                  </td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction
                            kind="restore"
                            label={`Restore ${row.name}`}
                            onClick={() => void handleRestore(row)}
                          />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${row.name}`} onClick={() => setEditItem(row)} />
                            <AdminRowAction kind="delete" label={`Delete ${row.name}`} onClick={() => setDeleteItem(row)} />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['group', 'groups']} />
        </>
      )}

      {showCreate && (
        <UserGroupModal
          allUsers={allUsers}
          onClose={() => setShowCreate(false)}
          onSaved={handleCreated}
        />
      )}

      {editItem && (
        <UserGroupModal
          userGroup={editItem}
          allUsers={allUsers}
          onClose={() => setEditItem(null)}
          onSaved={handleUpdated}
        />
      )}

      <ConfirmDialog
        isOpen={deleteItem !== null}
        title="Delete User Group"
        message={
          deleteItem
            ? `Are you sure you want to delete "${deleteItem.name}"?`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteItem(null)}
      />
    </AdminListPage>
  );
}
